import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { keys, useLocations, useMe } from "../api/hooks";
import { actionsResponseSchema, type ActionRow, type ActionType } from "../../api/contracts";
import { ACTION_LABELS, ACTION_TONE, buildActionsQuery, EMPTY_ACTIVITY, filterByStaff, groupByPass, isChange, PAGE_SIZE, summarizeLoaded, summarizePass, type ActivityFilters } from "../lib/activity";
import { formatDay, formatInstant, formatMinutes, formatPct } from "../lib/format";
import { explainReason, voiceOfAction } from "../lib/activity-reason";
import { useShellPrefix } from "../lib/shell";
import { Badge, Button, EmptyState, ErrorNote, Field, Input, PageHeader, Panel, Select, Spinner, Toggle } from "../components/ui";

const EDGE_LABEL = { start: "Start of shift", end: "End of shift" } as const;

/** One planner decision, laid out as the Figma row: who and what, then when and where, then why; the numbers at the right. */
function ActivityRow({ a, zone, admin }: { a: ActionRow; zone: string | undefined; admin: boolean }) {
  const why = explainReason({ reason: a.reason, edge: a.edge, gapMinutes: a.gapMinutes, utilizationPct: a.utilizationPct, voice: voiceOfAction(a.type) });
  return (
    <li className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-hs-border px-5 py-4">
      <div className="min-w-0 flex-[1_1_20rem] space-y-2">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="flex items-center gap-2">
            <span aria-hidden className="flex h-10 w-10 items-center justify-center rounded-full bg-hs-chip text-sm font-semibold text-hs-text">{a.staffName.trim().charAt(0).toUpperCase()}</span>
            <span className="text-sm font-semibold text-hs-text">{a.staffName}</span>
          </span>
          <Badge tone={ACTION_TONE[a.type]}>{ACTION_LABELS[a.type]}</Badge>
          {a.error && <span className="text-xs font-medium text-hs-danger">{a.error}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-hs-text">
          <span className="p-2">{formatDay(a.date)}</span>
          <span className="rounded-full bg-hs-primary-soft p-2 text-hs-primary">{EDGE_LABEL[a.edge]}</span>
          <span className="p-2">{a.locationName}</span>
        </div>
        <p className="text-sm text-hs-text">{why.sentence}</p>
        {admin && why.recognised && <p className="text-xs text-hs-text-muted">{a.reason}</p>}
      </div>
      <dl className="flex shrink-0 gap-6 text-right sm:text-left">
        <div>
          <dt className="text-xs font-semibold text-hs-text/70">Open</dt>
          <dd className="mt-1 whitespace-nowrap text-base font-semibold text-hs-text">{formatMinutes(a.gapMinutes)}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold text-hs-text/70">Booked</dt>
          <dd className="mt-1 text-base font-semibold text-hs-text">{formatPct(a.utilizationPct)}</dd>
        </div>
      </dl>
      <span className="sr-only">{formatInstant(a.createdAt, zone)}</span>
    </li>
  );
}

export function ActivityPage() {
  const me = useMe().data!;
  const admin = useShellPrefix() === "/admin";
  const locations = useLocations();
  const [f, setF] = useState<ActivityFilters>(EMPTY_ACTIVITY);
  const [changesOnly, setChangesOnly] = useState(false);
  const query = buildActionsQuery(f);
  const actions = useQuery({ queryKey: keys.actions(query), queryFn: () => api(`/api/actions${query}`, actionsResponseSchema) });
  const set = (k: keyof ActivityFilters) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value, page: 0 });
  const zoneOf = (blvdId: string) => locations.data?.find((l) => l.blvdId === blvdId)?.timezone;
  const loaded = filterByStaff(actions.data?.actions ?? [], f.staff);
  const rows = changesOnly ? loaded.filter((a) => isChange(a.type)) : loaded;
  const passes = groupByPass(rows);
  const total = actions.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <PageHeader title="Activity" subtitle={me.mode === "shadow" ? "Every decision the planner made. In preview mode these are what would have happened." : "How BTB blocks are added, removed and locked across your locations."} />
      <Panel padding="none" className="mb-7">
        <div className="grid gap-3 p-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <Field label="Location"><Select value={f.blvdId} onChange={set("blvdId")}><option value="">All</option>{(locations.data ?? []).map((l) => <option key={l.blvdId} value={l.blvdId}>{l.name}</option>)}</Select></Field>
          <Field label="Staff"><Input value={f.staff} onChange={set("staff")} placeholder="Search by name" /></Field>
          <Field label="From"><Input type="date" value={f.from} onChange={set("from")} /></Field>
          <Field label="To"><Input type="date" value={f.to} onChange={set("to")} /></Field>
          <Field label="What happened"><Select value={f.type} onChange={set("type")}><option value="">Anything</option>{(Object.keys(ACTION_LABELS) as ActionType[]).map((t) => <option key={t} value={t}>{ACTION_LABELS[t]}</option>)}</Select></Field>
          <Field label="Mode"><Select value={f.mode} onChange={set("mode")}><option value="">Both</option><option value="shadow">Preview</option><option value="live">Live</option></Select></Field>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-hs-border px-6 pb-6 pt-4">
          <span className="flex items-center gap-2">
            <Toggle checked={changesOnly} onChange={setChangesOnly} label="Changes only" />
            <span className="text-base font-semibold text-hs-text">Changes only</span>
          </span>
          {actions.data && <span className="text-sm font-medium text-hs-text-muted">{summarizeLoaded(loaded)}</span>}
        </div>
      </Panel>
      <ErrorNote error={actions.error} />
      {actions.isPending && <Spinner />}
      {actions.data && total === 0 && <EmptyState title="No activity yet" body="Once a location is turned on, each planning pass shows up here." />}
      {actions.data && total > 0 && loaded.length === 0 && <EmptyState title="No matches" body="Nothing on this page matches the staff filter. Clear it or move to another page." />}
      {actions.data && loaded.length > 0 && rows.length === 0 && <EmptyState title="No changes on this page" body="Every block here was left alone. Turn off Changes only to see them, or move to another page." />}
      {rows.length > 0 && (
        <Panel padding="none" className="overflow-hidden">
          <ul aria-label="Planner activity" className="-mt-px">
            {passes.map((p) => (
              <li key={p.key} className="list-none">
                <h2 className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-t border-hs-border bg-hs-surface-alt px-5 py-3">
                  <span className="text-sm font-semibold text-hs-text">{formatInstant(p.at, zoneOf(p.rows[0].blvdId))}</span>
                  <span className="text-xs font-semibold text-hs-text-muted">{summarizePass(p.rows)}</span>
                </h2>
                <ul>{p.rows.map((a) => <ActivityRow key={a.id} a={a} zone={zoneOf(a.blvdId)} admin={admin} />)}</ul>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-between text-sm text-hs-text-muted">
          <Button variant="ghost" size="sm" disabled={f.page === 0} onClick={() => setF({ ...f, page: f.page - 1 })}>Previous</Button>
          <span className="tabular-nums">Page {f.page + 1} of {pages} · {total} across all pages{f.staff.trim() ? ` · ${loaded.length} on this page match "${f.staff.trim()}"` : ""}</span>
          <Button variant="ghost" size="sm" disabled={f.page + 1 >= pages} onClick={() => setF({ ...f, page: f.page + 1 })}>Next</Button>
        </div>
      )}
    </>
  );
}
